import React, { useState, useMemo } from "react";

/**
 * CrossImpactAssessment — customer-facing view of the internal cross-impact matrix.
 *
 * METHOD CONCEALMENT: the underlying analysis is a DIME × PMESII-PTT cross-impact
 * matrix, computed and stored internally. This component NEVER names or reproduces
 * that framework. It collapses the actor-instrument (DIME) axis out of view and
 * presents only a per-domain severity heatmap with plain-language domain labels.
 * The doctrinal terms must not appear in any customer-facing string here.
 */

// Severity → the shared --threat-* ramp (single-sourced; values unchanged).
const SEV_COLORS = { RED: "var(--threat-critical)", YELLOW: "var(--threat-moderate)", GREEN: "var(--threat-low)" };
const sevColor = (s) => SEV_COLORS[String(s || "").toUpperCase()] || "#B0A699";
const sevLabel = (s) => {
  const u = String(s || "").toUpperCase();
  return u === "RED" ? "Critical" : u === "YELLOW" ? "Escalating" : u === "GREEN" ? "Stable" : "—";
};

// Internal PMESII-PTT column keys → concealed public labels (no doctrinal terms shown)
const DOMAIN_LABELS = {
  political: "Governance", governance: "Governance",
  military: "Security", security: "Security",
  economic: "Economy", economy: "Economy",
  social: "Society", society: "Society",
  information: "Information", informational: "Information",
  infrastructure: "Infrastructure",
  physical_environment: "Environment", physical: "Environment", environment: "Environment",
  time: "Horizon", horizon: "Horizon",
  technology: "Technology", tech: "Technology",
};
const DOMAIN_ORDER = ["Governance", "Security", "Economy", "Society", "Information", "Infrastructure", "Environment", "Horizon", "Technology"];
const sevRank = (s) => { const u = String(s || "").toUpperCase(); return u === "RED" ? 3 : u === "YELLOW" ? 2 : u === "GREEN" ? 1 : 0; };
const keyOf = (k) => DOMAIN_LABELS[String(k).toLowerCase().replace(/\s+/g, "_").replace(/[^a-z_]/g, "")];

/**
 * Collapse the matrix (any shape) to one entry per public domain, keeping the
 * worst-severity cell across the hidden instrument axis.
 */
function normalizeMatrix(matrix) {
  let m = matrix;
  if (typeof m === "string") { try { m = JSON.parse(m); } catch { return []; } }
  if (!m || typeof m !== "object") return [];

  const byLabel = {};
  const put = (label, cell) => {
    if (!label || !cell || typeof cell !== "object") return;
    const rank = sevRank(cell.severity);
    if (!byLabel[label] || rank > byLabel[label]._rank) {
      byLabel[label] = {
        domain: label, _rank: rank, severity: String(cell.severity || "").toUpperCase(),
        driver: cell.driver, indicator: cell.current_indicator || cell.indicator, risk: cell.strategic_risk || cell.risk,
      };
    }
  };

  for (const [k, v] of Object.entries(m)) {
    const label = keyOf(k);
    if (label && v && typeof v === "object" && ("severity" in v || "driver" in v || "strategic_risk" in v)) {
      put(label, v); // Case A: keyed by domain
    } else if (v && typeof v === "object") {
      for (const [dk, dv] of Object.entries(v)) put(keyOf(dk), dv); // Case B: instrument → domain grid
    }
  }
  return DOMAIN_ORDER.filter((d) => byLabel[d]).map((d) => byLabel[d]);
}

export default function CrossImpactAssessment({ matrix }) {
  const [open, setOpen] = useState(null);
  const domains = useMemo(() => normalizeMatrix(matrix), [matrix]);

  if (!domains.length) {
    const txt = typeof matrix === "string" ? matrix : "";
    return txt ? <div style={{ fontSize: 13, lineHeight: 1.6, color: "var(--color-text-primary)" }}>{txt}</div> : null;
  }

  const active = domains.find((d) => d.domain === open);

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 8 }}>
        {domains.map((d) => (
          <button
            key={d.domain}
            onClick={() => setOpen(open === d.domain ? null : d.domain)}
            style={{
              textAlign: "left", border: `1px solid ${sevColor(d.severity)}`, borderLeft: `4px solid ${sevColor(d.severity)}`,
              borderRadius: 4, background: open === d.domain ? "rgba(44,62,80,0.05)" : "#fff",
              padding: "8px 10px", cursor: "pointer", fontFamily: "'IBM Plex Sans', sans-serif",
            }}
            aria-expanded={open === d.domain}
          >
            <div style={{ fontSize: 12, fontWeight: 700, color: "#2C3E50" }}>{d.domain}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
              <span style={{ width: 9, height: 9, borderRadius: "50%", background: sevColor(d.severity), flexShrink: 0 }} />
              <span style={{ fontSize: 11, color: "#5A5344" }}>{sevLabel(d.severity)}</span>
            </div>
          </button>
        ))}
      </div>

      {active && (
        <div style={{ marginTop: 10, padding: 12, border: "1px solid #E5E0D6", borderRadius: 4, background: "#F9F7F3" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#2C3E50", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
            <span>{active.domain} ·</span>
            <span style={{ width: 9, height: 9, borderRadius: "50%", background: sevColor(active.severity), flexShrink: 0 }} />
            <span style={{ color: "#2C3E50" }}>{sevLabel(active.severity)}</span>
          </div>
          {active.driver && <div style={{ fontSize: 13, lineHeight: 1.5, marginBottom: 4 }}><strong>Driver:</strong> {active.driver}</div>}
          {active.indicator && <div style={{ fontSize: 13, lineHeight: 1.5, marginBottom: 4 }}><strong>Current indicator:</strong> {active.indicator}</div>}
          {active.risk && <div style={{ fontSize: 13, lineHeight: 1.5 }}><strong>Strategic risk:</strong> {active.risk}</div>}
        </div>
      )}
    </div>
  );
}
